/**
 * FixBillService
 * 
 * This service handles all fixed bill expense-related operations in the application.
 * It provides methods for managing fixed bill expenses, including creation, retrieval,
 * updating, and deletion of fixed bill entries. The service supports operations for
 * both individual matters and system-wide fixed bill management.
 * 
 * @class
 */

import HttpService from './HttpService';
import { pathFixBillExpenses, pathFixBillExpensesByMatter, pathFixBillExpenseById } from '@frontend/__settings__/septSoftwarePaths';

const http = new HttpService();

class FixBillService {
    /**
     * Retrieves all fixed bill expenses from the system
     * 
     * @returns {Promise<Array>} Array of fixed bill expense objects
     * @throws {Error} If the API request fails
     */
    static async getAllFixBillExpenses(): Promise<any[]> {
        const data = await http.get(`${pathFixBillExpenses}`);
        return data;
    }

    /**
     * Retrieves fixed bill expenses for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of fixed bill expenses for the matter
     * @throws {Error} If the API request fails
     */
    static async getFixBillExpensesByMatter(matterId: string): Promise<any[]> {
        const data = await http.get(`${pathFixBillExpensesByMatter(matterId)}`);
        return data;
    }

    /**
     * Adds a new fixed bill expense
     * 
     * @param {Object} expenseData - Fixed bill expense data
     * @param {string} expenseData.matterId - ID of the matter
     * @param {string} expenseData.name - Name of the expense
     * @param {number} expenseData.amount - Amount of the expense
     * @param {string} [expenseData.description] - Description of the expense
     * 
     * @returns {Promise<Object>} Created fixed bill expense object
     * @throws {Error} If the API request fails
     */
    static async addFixBillExpense(expenseData: any): Promise<any> {
        const data = await http.post(`${pathFixBillExpenses}`, expenseData);
        return data;
    }

    /**
     * Updates an existing fixed bill expense
     * 
     * @param {string} fbillId - ID of the fixed bill expense
     * @param {Object} expenseData - Updated fixed bill expense data
     * @returns {Promise<Object>} Updated fixed bill expense object
     * @throws {Error} If the API request fails
     */
    static async updateFixBillExpense(fbillId: string, expenseData: any): Promise<any> {
            const data = await http.put(`${pathFixBillExpenseById(fbillId)}`, expenseData);
            return data;
    }

    /**
     * Deletes a fixed bill expense
     * 
     * @param {string} fbillId - ID of the fixed bill expense to delete
     * @returns {Promise<Object>} Response indicating success or failure
     * @throws {Error} If the API request fails
     */
    static async deleteFixBillExpense(fbillId: string): Promise<any> {
            const data = await http.delete(`${pathFixBillExpenseById(fbillId)}`);
            return data;
    }
}

export default FixBillService;
